import _ from "underscore";
import { DataTransforms, Module } from "../types/TenantInterfaces";
import { callFunctionIfExists } from "./GeneralUtils";
import Constants from "./Constants";
import { generateTimePrefills } from "./QuickCreateUtils";
import { PrefillPair } from "../types/GlobalInterfaces";

export const invalidFields = [
  "id",
  "_id",
  "Record Name",
  "_RecordName",
  "LastUpdated By",
  "LastUpdated",
  "Created By",
  "Created",
  "Modified By",
  "Modified",
  "Remote Systems",
  "Tags",
  "RecordOwner",
  "_ModuleAPIName",
];

function cleanRecord(record: any) {
  return Object.keys(record).reduce((acc: any, key) => {
    if (!invalidFields.includes(key)) {
      acc[key] = record[key];
    }
    return acc;
  }, {});
}

export function cleanModuleFields(module: Module) {
  return module.Fields.filter(
    (field) => !invalidFields.includes(field.APIName)
  );
}

export function transformData(record: any, dataTransform: DataTransforms) {
  const cleanedRecord: any = cleanRecord(record);

  const out: Array<any> = [];
  const outRecord: any = {};

  dataTransform.Fields.forEach((dtf) => {
    outRecord[dtf.target] = dtf.value || cleanedRecord[dtf.source] || "";
  });

  Object.keys(cleanedRecord).forEach((key) => {
    out.push({ Field: key, Value: cleanedRecord[key] });
  });

  dataTransform?.Fields?.forEach((dtf) => {
    out.push({
      Field: dtf.target,
      Value: dtf.value || cleanedRecord[dtf.source] || "",
    });
  });

  return out;
}

export function getBulkDTPrefill({
  records,
  dataTransform,
  targetModule,
  sourceModule,
  onSuccess,
  onError,
}: {
  records: Array<Record<string, any>>;
  dataTransform: DataTransforms;
  targetModule: Module;
  sourceModule: Module;
  onSuccess?: (prefill: any) => void;
  onError?: (error: any) => void;
}) {
  try {
    let recordsOutWithPrefill: Array<Array<PrefillPair> | undefined> =
      records.map((record) => {
        return buildPrefill({
          record,
          dataTransform,
          targetModule,
          sourceModule: sourceModule,
        });
      });

    const allPayload: Array<Record<string, any>> = [];

    for (let i = 0; i < recordsOutWithPrefill.length; i++) {
      const newRecord = buildRecord({
        module: targetModule,
        prefill: recordsOutWithPrefill[i] ?? [],
      });
      newRecord._RecordName = records[i]._RecordName;
      newRecord._id = records[i].id || records[i]._id;
      newRecord.id = records[i].id || records[i]._id;
      allPayload.push(newRecord);
    }

    callFunctionIfExists(onSuccess, allPayload);
    return allPayload;
  } catch (error) {
    callFunctionIfExists(onError, error);
    console.error(error);
  }
}

export function buildRecord({
  prefill,
  module,
}: {
  prefill: PrefillPair[];
  module: Module;
}) {
  const newPayload: Record<string, any> = {};

  for (let i = 0; i < module.Fields.length; i++) {
    const field = module.Fields[i];
    const foundPrefill = prefill.find((p) => p.Field === field.APIName);

    if (foundPrefill?.Value) {
      newPayload[field.APIName] = foundPrefill?.Value;
    } else if (field?.DefaultValue && typeof field.DefaultValue === "string") {
      newPayload[field.APIName] = field.DefaultValue.startsWith("{{")
        ? generateDynamicDefVals(field.FieldType, field?.DefaultValue)
        : field?.DefaultValue;
    } else if (
      field?.DefaultValue !== undefined &&
      field?.DefaultValue !== null
    ) {
      newPayload[field.APIName] =
        field?.FieldType === "Calculated" && field?.DefaultValue?.Value
          ? field?.DefaultValue?.Value
          : field?.DefaultValue;
    } else {
      newPayload[field.APIName] = "";
    }
  }

  return newPayload;
}

export function buildPrefill({
  record,
  sourceModule,
  targetModule,
  dataTransform,
  onSuccess,
  onError,
}: {
  record: any;
  sourceModule?: Module | null;
  targetModule?: Module | null;
  dataTransform?: DataTransforms;
  onSuccess?: (prefill: any) => void;
  onError?: (error: any) => void;
}) {
  if (!record || !targetModule || !dataTransform) {
    callFunctionIfExists(onError, "Missing required parameters");
    return;
  }

  const prefill: PrefillPair[] = [];
  const sourceModuleFields = sourceModule?.Fields || [];
  const targetModuleFields = targetModule?.Fields || [];

  for (let i = 0; i < dataTransform?.Fields.length; i++) {
    const dtf = dataTransform?.Fields[i];

    if (dtf.source.startsWith("{{record.id}}")) {
      const referencePayload: any = {
        id: record._id,
        _ModuleAPIName: record._ModuleAPIName,
        _RecordName: record._RecordName,
      };

      prefill.push({
        Field: dtf.target,
        Value: referencePayload,
      });

      continue;
    }

    let sourceField = sourceModuleFields.find((f) => f.APIName === dtf.source);

    // Subitem Fields Transform
    if (dtf.source.includes(".")) {
      const [parent, child] = dtf.source.split(".");
      sourceField = sourceModuleFields
        .find((f) => f.APIName === parent)
        ?.SubitemFields?.find((sf) => sf.APIName === child);
    }

    const targetField = targetModuleFields.find(
      (f) => f.APIName === dtf.target
    );
    let value: any = record?.[sourceField?.APIName || ""] || null;

    // Legacy SingleFile Transform
    if (
      value &&
      sourceField?.FieldType === "SingleFile" &&
      !Array.isArray(value)
    ) {
      const newFiles = value.map((file: any) => {
        delete file.URL;
        return file;
      });
      value = newFiles;
    }

    // Single File Transform
    if (
      value &&
      sourceField?.FieldType === "SingleFile" &&
      Array.isArray(value)
    ) {
      const newFiles = value.map((file: any) => {
        delete file.URL;
        return file;
      });
      value = newFiles;
    }

    // Image, Video, Audio Transform
    if (
      ["Image", "Video", "Audio"].includes(sourceField?.FieldType || "") &&
      value
    ) {
      const newFile = structuredClone(value);
      delete newFile.URL;
      value = targetField?.FieldType === "SingleFile" ? [newFile] : newFile;
    }

    // Number to Text Transform
    if (sourceField?.FieldType === "Text" && typeof value === "number") {
      value = value.toString();
    }

    prefill.push({
      Field: dtf.target,
      Value: value,
    });
  }

  callFunctionIfExists(onSuccess, prefill);
  return prefill;
}

function generateDynamicDefVals(fieldType: string, value: any) {
  if (
    [Constants.FIELDTYPES.DATE, Constants.FIELDTYPES.DATE_TIME].includes(
      fieldType
    )
  ) {
    const timeDefault = generateTimePrefills(value, true);

    if (!timeDefault) return "";

    return timeDefault.Value || timeDefault.Max;
  }
  return value;
}
